import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
import TagManager from "@sooro-io/react-gtm-module";
import { useCookies } from "react-cookie";
// components
import AppHeader from "../AppHeader";
import AppFooter from "../AppFooter";
import AppAcceptCookiesBox from "../AppAcceptCookiesBox";
import AppReloadModal from "./AppReloadModal";
import GlobalLoader from "../GlobalLoader";
import AppBreadcrumbs from "../AppBreadcrumbs";
import AppHelmet from "../AppHelmet";
import NotFound from "../../pages/NotFound";
// hooks
import { useActions } from "../../common/hooks/useActions";
import { useScrollToTopOnTransportRoute } from "../../common/hooks/useScrollToTopOnTransportRoute";
import { useMarketplaceSelectOfferTracking } from "../../common/hooks/useMarketplaceSelectOfferTracking";
// actions
import {
  globalActionCreators,
  selectAppError,
  selectNewRelicBrowserConfig
} from "../../common/store/modules/globalModule";
import { tenantActionCreators } from "../../common/store/modules/tenantModule";
// helpers
import { getMainSubdomainUrl } from "../../common/utils/helpers/subdomainHelpers/getMainSubdomainUrl";
import { checkIsDefaultDomain } from "../../common/utils/helpers/subdomainHelpers/checkIsDefaultDomain";
import { initNewRelicFromAppConfig } from "../../common/utils/newRelic/initNewRelic";
import { resolveGtmInitArgs } from "../../common/utils/gtm/resolveGtmInitArgs";
// icons
import { logoIconDefault } from "../../common/vector";

export default function AppLayout() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies(["cookieAccepted"]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUpdateBumpFlag, setShowUpdateBumpFlag] = useState(false);
  const [isMajorVersionReloadRequired, setIsMajorVersionReloadRequired] = useState(false);

  const language = localStorage.getItem("i18nextLng");

  // actions
  const getConfig = useActions(globalActionCreators?.getConfigAction, []);
  const getTenantInfo = useActions(tenantActionCreators?.getTenantInfoAction, []);
  const clearAppError = useActions(globalActionCreators?.clearAppErrorAction, []);
  const getCurrentAppVersion = useActions(globalActionCreators?.getCurrentAppVersionAction, []);
  const checkAppVersionStatus = useActions(globalActionCreators?.checkAppVersionStatusAction, []);
  // states
  const globalLoader = useSelector((state: any) => state.global.globalLoader);
  const defaultDomain = useSelector((state: any) => state.global.defaultDomain);
  const gtmContainerId = useSelector((state: any) => state.global.gtmContainerId);
  const gtmAuth = useSelector((state: any) => state.global.gtmAuth);
  const gtmPreview = useSelector((state: any) => state.global.gtmPreview);
  const gtmInitializedRef = useRef(false);
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const currentAppVersion = useSelector((state: any) => state.global.currentAppVersion);
  const versionUpdateStatus = useSelector((state: any) => state.global.versionUpdateStatus);
  const platformType = useSelector((state: any) => state.global.platformType);
  const appError = useSelector(selectAppError);
  const {
    enabled: newRelicBrowserEnabled,
    applicationId: newRelicBrowserApplicationId,
    licenseKey: newRelicBrowserLicenseKey,
    accountId: newRelicBrowserAccountId,
    trustKey: newRelicBrowserTrustKey,
    agentId: newRelicBrowserAgentId,
    beacon: newRelicBrowserBeacon
  } = useSelector(selectNewRelicBrowserConfig);

  // helpers
  const isAirline = platformType === "airline";
  const isDefaultDomain: boolean = checkIsDefaultDomain(window.location.hostname, defaultDomain);
  const isNotFoundRoute: boolean = appError.active;
  const defaultAppHeaderColor = theme.palette.primary.dark;
  const defaultAppHeaderLogo = logoIconDefault.default;

  useScrollToTopOnTransportRoute();
  useMarketplaceSelectOfferTracking();

  useEffect(() => {
    getConfig();
    getCurrentAppVersion();
  }, []);

  useEffect(() => {
    // Set interval to check version every minute
    const interval = setInterval(function () {
      checkAppVersionStatus();
    }, 60000);

    if (versionUpdateStatus?.status && !showUpdateBumpFlag) {
      setIsModalOpen(true);
      if (versionUpdateStatus?.version === "major") {
        setIsMajorVersionReloadRequired(true);
      }
    }

    // clear interval
    if (showUpdateBumpFlag) {
      clearInterval(interval);
    }
    return () => {
      clearInterval(interval);
    };
  }, [showUpdateBumpFlag, currentAppVersion, versionUpdateStatus]);

  useEffect(() => {
    // call hotel info
    if (!isDefaultDomain && defaultDomain) {
      const tenantUrl: string = getMainSubdomainUrl(window.location.hostname, defaultDomain);
      getTenantInfo(tenantUrl, true, navigate);
    }
  }, [defaultDomain]);

  // recover from the inline error view whenever the user navigates to another path
  useEffect(() => {
    clearAppError();
  }, [location.pathname]);

  // Google Tag Manager Initialization
  useEffect(() => {
    const gtmInitArgs = resolveGtmInitArgs({ gtmId: gtmContainerId, auth: gtmAuth, preview: gtmPreview });

    if (gtmInitArgs && cookies.cookieAccepted && !gtmInitializedRef.current) {
      TagManager.initialize(gtmInitArgs);
      gtmInitializedRef.current = true;
    }
  }, [gtmContainerId, gtmAuth, gtmPreview, cookies.cookieAccepted]);

  // New Relic Browser Monitoring initialization
  useEffect(() => {
    initNewRelicFromAppConfig({
      enabled: newRelicBrowserEnabled,
      applicationId: newRelicBrowserApplicationId,
      licenseKey: newRelicBrowserLicenseKey,
      accountId: newRelicBrowserAccountId,
      trustKey: newRelicBrowserTrustKey,
      agentId: newRelicBrowserAgentId,
      beacon: newRelicBrowserBeacon
    });
  }, [
    newRelicBrowserEnabled,
    newRelicBrowserApplicationId,
    newRelicBrowserLicenseKey,
    newRelicBrowserAccountId,
    newRelicBrowserTrustKey,
    newRelicBrowserAgentId,
    newRelicBrowserBeacon
  ]);

  const handleExploreIconClick = () => {
    navigate(`/${language}`, { replace: true });
  };

  return (
    <div className="app-layout" data-cy="guest">
      {/* Set metadata */}
      <AppHelmet isDefaultDomain={isDefaultDomain} />
      {/* // */}

      {showUpdateBumpFlag && (
        <Box
          className="update-flag"
          onClick={() => {
            localStorage.clear();
            window.location.reload();
          }}
        >
          <Box sx={{ position: "relative" }}>
            {t("version.updateFlag")}{" "}
            <span className="update-flag__description">{t("version.updateFlagDescription")}</span>
          </Box>
          <span className="shape"></span>
        </Box>
      )}
      {/* show global loader only for hotels (subdomains) */}
      {globalLoader && !isDefaultDomain && <GlobalLoader />}
      {isDefaultDomain ? (
        <AppHeader color={defaultAppHeaderColor} logo={defaultAppHeaderLogo} showLanguageIcon={!isNotFoundRoute} />
      ) : (
        <AppHeader
          poweredByLogo={tenantInfo?.style?.powered_by_logo}
          color={isNotFoundRoute || !tenantInfo ? defaultAppHeaderColor : tenantInfo?.style?.page?.main_color}
          logo={isNotFoundRoute || !tenantInfo ? defaultAppHeaderLogo : tenantInfo?.style?.logo}
          showLanguageIcon={!isNotFoundRoute && !isAirline}
          showExploreIcon={!isNotFoundRoute && !isAirline}
          handleExploreIconClick={handleExploreIconClick}
          menuLinks={tenantInfo?.menu?.links}
        />
      )}
      {!isDefaultDomain && <AppBreadcrumbs />}
      <Box className="app-container">
        {appError.active ? <NotFound /> : <Outlet />}
        <AppReloadModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isMajorVersionReloadRequired={isMajorVersionReloadRequired}
          setShowUpdateBumpFlag={setShowUpdateBumpFlag}
        />
        {typeof cookies.cookieAccepted !== "boolean" && <AppAcceptCookiesBox />}
      </Box>
      {<AppFooter isDefaultDomain={isDefaultDomain} />}
    </div>
  );
}
